import React, { ComponentProps } from 'react';
import { ISonURL } from '../../../../types/IUrl';
import { asView } from '../../../ViewFetcher';
import { useLayoutGroupContext } from '../../../../components/blocks/LayoutGroup/LayoutGroup';
import { MediaInformation } from '../../../../components/organisms/MediaInformation';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { IFrame, IFrameContainer } from '../../IFrame';
import { Modifier } from '../../../../lib/bemUtils/Modifier';
import { IFrameModifier } from '../../IFrame/IFrame';
import { StatusInfoError } from '../../StatusInfo';
import { StatusInfoModifier } from '../../StatusInfo/partials/StatusInfo';
import { IFileItem } from '../../../../types/api/FileItem';
import { IFrameContainerModifier } from '../../IFrame/IFrameContainer';

const getHostnameFromUrl = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

export type ExternalContentDefaultViewProps = {
  iframe: ISonURL & {
    title: string;
    caption?: string;
    size?: 'large' | 'medium' | 'small';
    orientation: 'portrait' | 'landscape';
    popUp?: boolean;
    popUpUrl?: string;
  };
  notAvailable?: boolean;
  fileLinkItem?: IFileItem;
};

function ExternalContentDefault({ iframe, notAvailable }: ExternalContentDefaultViewProps) {
  const groupLayout = useLayoutGroupContext();
  const { t } = useSonTranslation('ExternalContent');

  if (notAvailable) {
    return (
      <div className="external-content__error">
        <StatusInfoError
          message={t(`input.fileResourceReference.notAvailable`)}
          modifier={[StatusInfoModifier.RED, StatusInfoModifier.ALIGN_LEFT, StatusInfoModifier.MARGIN_BOTTOM]}
        />
      </div>
    );
  }
  const { url, title, caption, size, popUp, orientation, popUpUrl } = iframe;
  const iFrameTitle = title || t('externalContentOfUrl', { url: getHostnameFromUrl(url) });

  const getModifier = (orientationPar: 'portrait' | 'landscape'): Modifier<IFrameModifier> | undefined => {
    if (orientationPar === 'portrait') {
      return IFrameModifier.PORTRAIT;
    }
    return undefined;
  };

  return (
    <MediaInformation
      media={{
        size,
        caption,
        contentType: 'iframe',
      }}
      hasPopUp={popUp}
      popUpContent={
        <IFrameContainer>
          <IFrame title={iFrameTitle} src={popUp && popUpUrl ? popUpUrl : url} modifier={getModifier(orientation)} />
        </IFrameContainer>
      }
      groupLayout={groupLayout}
    >
      <IFrameContainer modifier={IFrameContainerModifier.NO_PADDING}>
        <IFrame title={iFrameTitle} src={url} modifier={getModifier(orientation)} />
      </IFrameContainer>
    </MediaInformation>
  );
}

export const ExternalContentDefaultView = asView(ExternalContentDefault);
export type ExternalContentDefaultView = ComponentProps<typeof ExternalContentDefaultView>;
